import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { getTeacherSubjects, getLiveClasses, createLiveClass, deleteLiveClass, addRecording, formatApiError } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Plus, Trash2, Video, Calendar, Clock, Loader2, Play, Film, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function TeacherClasses() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRecordingDialog, setShowRecordingDialog] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [formData, setFormData] = useState({ subject_id: '', title: '', scheduled_at: '', duration_minutes: '60' });
  const [recordingData, setRecordingData] = useState({ recording_url: '', recording_title: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');

  const fetchData = async () => {
    try {
      const subjectsData = await getTeacherSubjects();
      setSubjects(subjectsData);
      const allClasses = [];
      for (const sub of subjectsData) {
        const cls = await getLiveClasses(null, sub.id);
        allClasses.push(...cls);
      }
      allClasses.sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
      setClasses(allClasses);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      await createLiveClass({
        subject_id: formData.subject_id, title: formData.title,
        scheduled_at: new Date(formData.scheduled_at).toISOString(),
        duration_minutes: parseInt(formData.duration_minutes)
      });
      toast.success('Clase creada exitosamente');
      setShowCreateDialog(false);
      setFormData({ subject_id: '', title: '', scheduled_at: '', duration_minutes: '60' });
      fetchData();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const handleDeleteClass = async (id) => {
    if (!window.confirm('¿Eliminar esta clase?')) return;
    try { await deleteLiveClass(id); toast.success('Clase eliminada'); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const handleAddRecording = async () => {
    if (!selectedClass) return;
    setSubmitting(true);
    try {
      await addRecording(selectedClass.id, recordingData.recording_url, recordingData.recording_title);
      toast.success('Grabación agregada');
      setShowRecordingDialog(false);
      setSelectedClass(null);
      setRecordingData({ recording_url: '', recording_title: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const filteredClasses = filterSubject === 'all' ? classes : classes.filter(c => c.subject_id === filterSubject);

  if (loading) return <Layout><div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="teacher-classes-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">Gestión de Clases</h1>
            <p className="text-[#737373] mt-2">{filteredClasses.length} clases</p>
          </div>
          <div className="flex gap-3">
            {subjects.length > 0 && (
              <Select value={filterSubject} onValueChange={setFilterSubject}>
                <SelectTrigger className="w-48 border-[#E5DFD3]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los cursos</SelectItem>
                  {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button data-testid="create-class-btn" onClick={() => setShowCreateDialog(true)} disabled={subjects.length === 0} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
              <Plus className="h-4 w-4 mr-2" /> Nueva Clase
            </Button>
          </div>
        </div>

        {subjects.length === 0 && (
          <Alert className="mb-6 bg-[#B8860B]/10 border-[#B8860B]/20">
            <AlertDescription className="text-[#B8860B]">No tienes cursos asignados. El administrador te asignará cursos para programar clases.</AlertDescription>
          </Alert>
        )}

        {filteredClasses.length > 0 ? (
          <div className="space-y-4">
            {filteredClasses.map((cls) => {
              const isPast = new Date(cls.scheduled_at) < new Date();
              return (
                <Card key={cls.id} data-testid={`class-card-${cls.id}`} className={`bg-white border border-[#E5DFD3] shadow-sm ${isPast ? 'opacity-80' : ''}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${isPast ? 'bg-[#E5DFD3]' : 'bg-[#8B1538]/10'}`}>
                          {cls.recording_url ? <Film className="h-6 w-6 text-[#2D6A4F]" /> : <Video className={`h-6 w-6 ${isPast ? 'text-[#737373]' : 'text-[#8B1538]'}`} />}
                        </div>
                        <div>
                          <h3 className="font-medium text-[#1A1A1A]">{cls.title}</h3>
                          {cls.subject_title && <p className="text-xs text-[#8B1538] font-medium">{cls.subject_title}</p>}
                          <div className="flex items-center gap-4 mt-1 text-sm text-[#737373]">
                            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(cls.scheduled_at).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {new Date(cls.scheduled_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                            <span>{cls.duration_minutes} min</span>
                          </div>
                          {cls.recording_url && (
                            <a href={cls.recording_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#2D6A4F] font-medium mt-1 hover:underline">
                              <ExternalLink className="h-3 w-3" /> Ver grabación
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {isPast && !cls.recording_url && (
                          <Button size="sm" variant="outline" onClick={() => { setSelectedClass(cls); setRecordingData({ recording_url: '', recording_title: cls.title + ' - Grabación' }); setShowRecordingDialog(true); }} className="border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F]/10">
                            <Film className="h-4 w-4 mr-1" /> Grabación
                          </Button>
                        )}
                        {!isPast && (
                          <Button size="sm" onClick={() => navigate(`/teacher/class/${cls.id}`)} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
                            <Play className="h-4 w-4 mr-1" /> Iniciar
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteClass(cls.id)} className="text-[#9B2226] hover:bg-[#9B2226]/10">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <Video className="h-16 w-16 mx-auto text-[#D9B48F] mb-4" />
            <h3 className="font-heading text-xl text-[#1A1A1A] mb-2">No hay clases programadas</h3>
          </div>
        )}

        {/* Create Class Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Programar Clase</DialogTitle>
              <DialogDescription>Programa una nueva clase para uno de tus cursos</DialogDescription>
            </DialogHeader>
            {error && <Alert variant="destructive" className="bg-[#9B2226]/10 border-[#9B2226]/20 text-[#9B2226]"><AlertDescription>{error}</AlertDescription></Alert>}
            <form onSubmit={handleCreateClass} className="space-y-4">
              <div className="space-y-2">
                <Label>Curso</Label>
                <Select value={formData.subject_id} onValueChange={(v) => setFormData({...formData, subject_id: v})}>
                  <SelectTrigger data-testid="class-subject-select" className="border-[#E5DFD3]"><SelectValue placeholder="Selecciona un curso" /></SelectTrigger>
                  <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.title} ({s.module_title})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Título de la Clase</Label>
                <Input data-testid="class-title-input" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="border-[#E5DFD3]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha y Hora</Label>
                  <Input data-testid="class-datetime-input" type="datetime-local" value={formData.scheduled_at} onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})} required className="border-[#E5DFD3]" />
                </div>
                <div className="space-y-2">
                  <Label>Duración (min)</Label>
                  <Input type="number" value={formData.duration_minutes} onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})} className="border-[#E5DFD3]" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-[#E5DFD3]">Cancelar</Button>
                <Button type="submit" disabled={submitting || !formData.subject_id} className="bg-[#8B1538] hover:bg-[#70102B] text-white">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Programar Clase'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Recording Dialog */}
        <Dialog open={showRecordingDialog} onOpenChange={setShowRecordingDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Agregar Grabación</DialogTitle>
              <DialogDescription>Agrega el enlace de la grabación para "{selectedClass?.title}"</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL de la Grabación</Label>
                <Input data-testid="recording-url-input" value={recordingData.recording_url} onChange={(e) => setRecordingData({...recordingData, recording_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." required className="border-[#E5DFD3]" />
              </div>
              <div className="space-y-2">
                <Label>Título (opcional)</Label>
                <Input value={recordingData.recording_title} onChange={(e) => setRecordingData({...recordingData, recording_title: e.target.value})} className="border-[#E5DFD3]" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRecordingDialog(false)} className="border-[#E5DFD3]">Cancelar</Button>
              <Button onClick={handleAddRecording} disabled={!recordingData.recording_url || submitting} className="bg-[#8B1538] hover:bg-[#70102B] text-white">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
